/**
 * Debounced booking URL availability check for v2 onboarding.
 * Performs real Supabase uniqueness checks.
 * Excludes the current user's own profile from "taken" results (resume sessions).
 */
import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';

export type UrlCheckStatus = 'idle' | 'checking' | 'available' | 'taken' | 'invalid';

export interface UrlCheckResult {
  status: UrlCheckStatus;
  message: string;
  ok: boolean; // true only when status === 'available'
}

const FORMAT_RE = /^[a-z0-9-]+$/;
const MIN_LEN = 3;
const MAX_LEN = 40;
const DEBOUNCE_MS = 550;

function validateFormat(slug: string): UrlCheckResult | null {
  if (!slug) return { status: 'idle', message: '', ok: false };
  if (!FORMAT_RE.test(slug)) {
    return { status: 'invalid', message: 'Lowercase letters, numbers, and hyphens only.', ok: false };
  }
  if (slug.length < MIN_LEN) {
    return { status: 'invalid', message: `At least ${MIN_LEN} characters required.`, ok: false };
  }
  if (slug.length > MAX_LEN) {
    return { status: 'invalid', message: `Maximum ${MAX_LEN} characters.`, ok: false };
  }
  return null; // passes format validation
}

export function useV2BookingUrlCheck(
  slug: string,
  currentUserId?: string | null
): UrlCheckResult {
  const [result, setResult] = useState<UrlCheckResult>({ status: 'idle', message: '', ok: false });
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Track slug+status of last confirmed DB check to avoid redundant network calls.
  // Using refs avoids adding `result` to the effect dependency array.
  const lastCheckedSlugRef = useRef<string>('');
  const lastCheckedStatusRef = useRef<UrlCheckStatus>('idle');

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);

    // Empty input — reset to idle immediately.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (!slug) { setResult({ status: 'idle', message: '', ok: false }); return; }

    // Synchronous format/length validation — no network call needed.
    const formatError = validateFormat(slug);
    if (formatError) { setResult(formatError); return; }

    // Skip re-check if we already confirmed this slug as available.
    if (slug === lastCheckedSlugRef.current && lastCheckedStatusRef.current === 'available') return;

    // Show "checking" immediately while the debounce timer counts down.
    setResult({ status: 'checking', message: 'Checking availability…', ok: false });

    timerRef.current = setTimeout(async () => {
      try {
        const { data, error } = await supabase
          .from('business_profiles')
          .select('id, user_id')
          .eq('booking_url', slug)
          .limit(1)
          .maybeSingle();

        if (error) {
          console.warn('[useV2BookingUrlCheck] Supabase error:', error.message);
          setResult({ status: 'invalid', message: 'Could not check URL availability. Try again.', ok: false });
          lastCheckedSlugRef.current = '';
          return;
        }

        let next: UrlCheckResult;
        if (data) {
          // Taken — unless the row belongs to the current user (resume session)
          if (currentUserId && data.user_id === currentUserId) {
            next = { status: 'available', message: 'Your current booking URL.', ok: true };
          } else {
            next = { status: 'taken', message: 'This URL is already taken. Try another.', ok: false };
          }
        } else {
          next = { status: 'available', message: 'This URL is available.', ok: true };
        }

        setResult(next);
        lastCheckedSlugRef.current = slug;
        lastCheckedStatusRef.current = next.status;
      } catch (err) {
        console.warn('[useV2BookingUrlCheck] Unexpected error:', err);
        setResult({ status: 'invalid', message: 'Could not check URL availability. Try again.', ok: false });
        lastCheckedSlugRef.current = '';
      }
    }, DEBOUNCE_MS);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [slug, currentUserId]);

  return result;
}
